import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Star, ShieldCheck, ThermometerSnowflake, EyeOff, Phone, Mail, MapPin, Menu, X, Award, Clock } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [currentBgIndex, setCurrentBgIndex] = useState(0);
  const [formData, setFormData] = useState({
    customer_name: "",
    email: "",
    phone: "",
    year: "",
    make: "",
    model: "",
    service_type: "",
    additional_notes: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Luxury car background carousel - expanded collection
  const heroBackgrounds = [
    { url: "https://images.pexels.com/photos/36237587/pexels-photo-36237587.jpeg?auto=compress&cs=tinysrgb&w=1920", name: "Porsche GT3 RS" },
    { url: "https://images.unsplash.com/photo-1685052189022-63e69ec3370e?w=1920", name: "Bugatti Chiron in Showroom" },
    { url: "https://images.pexels.com/photos/11963313/pexels-photo-11963313.jpeg?auto=compress&cs=tinysrgb&w=1920", name: "Lamborghini Collection" },
    { url: "https://images.pexels.com/photos/3894079/pexels-photo-3894079.jpeg?auto=compress&cs=tinysrgb&w=1920", name: "Rolls Royce" },
    { url: "https://images.pexels.com/photos/29918150/pexels-photo-29918150.jpeg?auto=compress&cs=tinysrgb&w=1920", name: "McLaren 720S Doors Up" },
    { url: "https://images.unsplash.com/photo-1737134366855-5fc56bdfd048?w=1920", name: "Orange McLaren F1" },
    { url: "https://images.pexels.com/photos/12499799/pexels-photo-12499799.jpeg?auto=compress&cs=tinysrgb&w=1920", name: "Yellow Lamborghini" },
    { url: "https://images.pexels.com/photos/8664306/pexels-photo-8664306.jpeg?auto=compress&cs=tinysrgb&w=1920", name: "Black Lamborghini Aventador" },
    { url: "https://images.unsplash.com/photo-1626141255994-59aa5d39da8a?w=1920", name: "McLaren Sports Car" }
  ];

  useEffect(() => {
    fetchReviews();
    fetchGallery();
    
    // Auto-rotate backgrounds every 5 seconds
    const bgInterval = setInterval(() => {
      setCurrentBgIndex((prevIndex) => (prevIndex + 1) % heroBackgrounds.length);
    }, 5000);

    return () => clearInterval(bgInterval);
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error("Error fetching reviews:", error);
    }
  };

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setGallery(response.data);
    } catch (error) {
      console.error("Error fetching gallery:", error);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (value) => {
    setFormData({ ...formData, service_type: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/quote`, formData);
      toast.success("Quote request submitted! We'll contact you soon.", {
        duration: 4000,
      });
      setFormData({
        customer_name: "",
        email: "",
        phone: "",
        year: "",
        make: "",
        model: "",
        service_type: "",
        additional_notes: ""
      });
    } catch (error) {
      toast.error("Failed to submit quote. Please try again.");
      console.error("Error submitting quote:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    element?.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  // Extended gallery with user's actual work photos
  const displayGallery = gallery.length > 0 ? gallery : [
    { id: "1", image_url: "https://customer-assets.emergentagent.com/job_lc-customs-tint/artifacts/uj2vdthk_IMG_5239%202.jpeg", alt_text: "White Mercedes G-Wagon - Premium Ceramic Tint" },
    { id: "2", image_url: "https://customer-assets.emergentagent.com/job_lc-customs-tint/artifacts/4jie3zrb_IMG_5265.jpeg", alt_text: "Silver McLaren 570S Spider - Premium Window Treatment" },
    { id: "3", image_url: "https://customer-assets.emergentagent.com/job_lc-customs-tint/artifacts/ypdauz10_IMG_5240%202.jpeg", alt_text: "Black Cadillac Escalade - Full Window Tint" },
    { id: "4", image_url: "https://customer-assets.emergentagent.com/job_lc-customs-tint/artifacts/gxwrdu1j_IMG_5241%202.jpeg", alt_text: "Exotic Supercar - Studio Finish with Premium Tint" },
    { id: "5", image_url: "https://customer-assets.emergentagent.com/job_lc-customs-tint/artifacts/uwmqu3tg_IMG_5243%202.jpeg", alt_text: "Black Chrysler 300 - Dark Ceramic Tint" },
    { id: "6", image_url: "https://customer-assets.emergentagent.com/job_lc-customs-tint/artifacts/8ppfdh1c_IMG_5244.jpeg", alt_text: "Black Corvette - Professional Studio Tint" },
    { id: "7", image_url: "https://customer-assets.emergentagent.com/job_lc-customs-tint/artifacts/m38rn2ty_IMG_5245.jpeg", alt_text: "White Audi A6 - Premium Studio Treatment" },
    { id: "8", image_url: "https://customer-assets.emergentagent.com/job_lc-customs-tint/artifacts/irkc0qy1_IMG_5246.jpeg", alt_text: "Silver Audi A6 - Premium Window Treatment" },
    { id: "9", image_url: "https://customer-assets.emergentagent.com/job_lc-customs-tint/artifacts/bbyudr7e_IMG_5247%202.jpeg", alt_text: "BMW - Professional Tint Installation" }
  ];

  // Real customer reviews
  const displayReviews = reviews.length > 0 ? reviews : [
    { id: "1", customer_name: "Eduardo Gonzalez", rating: 5, comment: "LC Customs did an amazing job on my tint! Super professional, quick turnaround, and the quality is flawless — no bubbles, clean edges, and it completely upgraded the look of my car. Pricing was fair and the service was top tier. Highly recommend if you want your tint done right the first time!" },
    { id: "2", customer_name: "Jovanny Verdin", rating: 5, comment: "Best service I've had done to my car! Really showed me the difference between his work and others work. 100% recommend, good prices" },
    { id: "3", customer_name: "Amaya Lozada", rating: 5, comment: "I got my front two windows done to match the back and they came out amazing! He did a great job, clean work, and done in a timely manner would definitely recommend!" },
    { id: "4", customer_name: "Duramax6.6 Diesel", rating: 5, comment: "They did a great job on my Jeep. Very happy with my new tints. Thanks Danny!" },
    { id: "5", customer_name: "Manny Perez", rating: 5, comment: "10/10 experience. Amazing customer service and tint quality is beautiful." },
    { id: "6", customer_name: "Christopher Damazo", rating: 5, comment: "LC customs for an all around window tint job work came out amazing and offers warranty with ceramic tint jobs which is the best part, would recommend for anyone who wants a quick and great tint quality." },
    { id: "7", customer_name: "Tra6soulromvnn", rating: 5, comment: "Couldn't be more happier than going with LC Customs, amazing service and punctual. Additionally, great with prices and delivering excellence!" },
    { id: "8", customer_name: "Jesus Barrera", rating: 5, comment: "I've taken all 3 of my cars to him, amazing job on all of them. Highly recommend if you need window tints done." }
  ];

  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-black/90 backdrop-blur-xl border-b border-gold/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            <img 
              src="https://customer-assets.emergentagent.com/job_lc-customs-tint/artifacts/j8hadcd4_IMG_1953.jpeg" 
              alt="LC Customs Logo" 
              className="h-14 w-auto cursor-pointer"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            />
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('about')} className="text-white/70 hover:text-gold transition-colors font-medium">About</button>
              <button onClick={() => scrollToSection('benefits')} className="text-white/70 hover:text-gold transition-colors font-medium">Benefits</button>
              <button onClick={() => scrollToSection('gallery')} className="text-white/70 hover:text-gold transition-colors font-medium">Gallery</button>
              <button onClick={() => scrollToSection('reviews')} className="text-white/70 hover:text-gold transition-colors font-medium">Reviews</button>
              <Button onClick={() => scrollToSection('quote')} data-testid="nav-quote-btn" className="bg-gradient-to-r from-purple to-purple-dark hover:from-purple-dark hover:to-purple rounded-full px-6 shadow-lg shadow-purple/30">
                Get a Quote
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-white">
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-4">
              <button onClick={() => scrollToSection('about')} className="block w-full text-left text-white/70 hover:text-gold transition-colors py-2">About</button>
              <button onClick={() => scrollToSection('benefits')} className="block w-full text-left text-white/70 hover:text-gold transition-colors py-2">Benefits</button>
              <button onClick={() => scrollToSection('gallery')} className="block w-full text-left text-white/70 hover:text-gold transition-colors py-2">Gallery</button>
              <button onClick={() => scrollToSection('reviews')} className="block w-full text-left text-white/70 hover:text-gold transition-colors py-2">Reviews</button>
              <Button onClick={() => scrollToSection('quote')} className="w-full bg-gradient-to-r from-purple to-purple-dark rounded-full">Get a Quote</Button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Images with Smooth Transition */}
        {heroBackgrounds.map((bg, index) => (
          <div
            key={index}
            className="absolute inset-0 transition-opacity duration-1000"
            style={{
              backgroundImage: `url(${bg.url})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: currentBgIndex === index ? 1 : 0,
              zIndex: currentBgIndex === index ? 1 : 0
            }}
          />
        ))}
        <div className="absolute inset-0 bg-black/60 z-10"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/80 z-10"></div>
        
        <div className="relative z-20 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <div className="mb-8">
            <div className="relative h-32 w-32 mx-auto mb-6 bg-gradient-to-br from-purple/10 to-gold/10 rounded-3xl p-4 backdrop-blur-sm border border-gold/20">
              <img 
                src="https://customer-assets.emergentagent.com/job_lc-customs-tint/artifacts/i8uumamn_9A5AC0CA-B76B-4108-B1ED-E38789AFB9CC.png" 
                alt="LC Star Logo" 
                className="h-full w-full object-contain"
              />
            </div>
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 tracking-tight">
            Experience the Art of <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple via-gold to-purple">Luxury</span>
          </h1>
          <p className="text-xl sm:text-2xl text-white/80 mb-8 max-w-3xl mx-auto">
            Elite Window Tinting in Chicago · Hitek Ceramic IR Technology
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => scrollToSection('quote')} data-testid="hero-quote-btn" size="lg" className="bg-gradient-to-r from-purple to-purple-dark hover:from-purple-dark hover:to-purple text-white rounded-full px-8 py-6 text-lg font-bold shadow-lg shadow-purple/40 hover:shadow-purple/60 transition-all">
              Get a Free Quote
            </Button>
            <Button onClick={() => scrollToSection('about')} data-testid="hero-learn-btn" size="lg" variant="outline" className="border-gold/30 text-white hover:bg-gold/10 hover:border-gold/50 rounded-full px-8 py-6 text-lg backdrop-blur-sm">
              Learn More
            </Button>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-gold/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-gold/70 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Meet the Expert */}
      <section id="about" className="py-24 bg-surface">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">Meet the Expert</h2>
              <h3 className="text-2xl text-gold font-semibold mb-4">Danny - Owner & Master Technician, LC Customs</h3>
              <p className="text-white/70 text-lg mb-4 leading-relaxed">
                With over <span className="text-gold font-semibold">5+ years of professional experience</span> in window tinting, Danny has built <span className="text-white font-semibold">LC Customs</span> into Chicago's most trusted name for premium automotive tinting services.
              </p>
              <p className="text-white/70 text-lg mb-6 leading-relaxed">
                Known for his <span className="text-white font-medium">meticulous attention to detail</span> and friendly, customer-first approach, Danny at <span className="text-white font-semibold">LC Customs</span> ensures every vehicle receives the highest quality treatment. His expertise with Hitek Ceramic IR films and commitment to perfection has earned him a loyal customer base throughout Chicago.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="bg-white/5 backdrop-blur-md border border-gold/20 rounded-xl px-6 py-4 hover:border-gold/40 transition-colors">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="text-gold" size={20} />
                    <div className="text-3xl font-bold text-gold">5+</div>
                  </div>
                  <div className="text-white/60 text-sm">Years Experience</div>
                </div>
                <div className="bg-white/5 backdrop-blur-md border border-gold/20 rounded-xl px-6 py-4 hover:border-gold/40 transition-colors">
                  <div className="flex items-center gap-2 mb-1">
                    <Award className="text-gold" size={20} />
                    <div className="text-3xl font-bold text-gold">100%</div>
                  </div>
                  <div className="text-white/60 text-sm">Satisfaction</div>
                </div>
                <div className="bg-white/5 backdrop-blur-md border border-gold/20 rounded-xl px-6 py-4 hover:border-gold/40 transition-colors">
                  <div className="flex items-center gap-2 mb-1">
                    <ShieldCheck className="text-gold" size={20} />
                    <div className="text-3xl font-bold text-gold">500+</div>
                  </div>
                  <div className="text-white/60 text-sm">Cars Perfected</div>
                </div>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-purple/30 to-gold/30 blur-3xl rounded-full"></div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_lc-customs-tint/artifacts/az2lw40z_DSC09424%202.jpeg" 
                  alt="Danny working on a vehicle" 
                  className="relative rounded-2xl w-full h-auto shadow-[0_8px_32px_rgba(0,0,0,0.5)] border border-gold/10"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-24 bg-base">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">Why Choose Hitek Ceramic IR?</h2>
            <p className="text-white/60 text-lg">The Ultimate Protection for Your Vehicle at LC Customs</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white/5 backdrop-blur-md border border-gold/10 rounded-2xl p-8 hover:border-purple/50 hover:scale-105 transition-all duration-300 group">
              <div className="bg-gradient-to-br from-purple/20 to-gold/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:from-purple/30 group-hover:to-gold/30 transition-colors">
                <ThermometerSnowflake className="text-gold" size={32} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Heat Rejection</h3>
              <p className="text-white/60 leading-relaxed">Advanced ceramic technology blocks up to 88% of infrared heat, keeping your car cool and comfortable.</p>
            </div>

            <div className="bg-white/5 backdrop-blur-md border border-gold/10 rounded-2xl p-8 hover:border-purple/50 hover:scale-105 transition-all duration-300 group">
              <div className="bg-gradient-to-br from-purple/20 to-gold/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:from-purple/30 group-hover:to-gold/30 transition-colors">
                <ShieldCheck className="text-gold" size={32} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">UV Protection</h3>
              <p className="text-white/60 leading-relaxed">99% UV protection prevents interior fading and protects your skin from harmful rays.</p>
            </div>

            <div className="bg-white/5 backdrop-blur-md border border-gold/10 rounded-2xl p-8 hover:border-purple/50 hover:scale-105 transition-all duration-300 group">
              <div className="bg-gradient-to-br from-purple/20 to-gold/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:from-purple/30 group-hover:to-gold/30 transition-colors">
                <EyeOff className="text-gold" size={32} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Privacy & Security</h3>
              <p className="text-white/60 leading-relaxed">Enhanced privacy without compromising visibility. See out clearly while others can't see in.</p>
            </div>

            <div className="bg-white/5 backdrop-blur-md border border-gold/10 rounded-2xl p-8 hover:border-purple/50 hover:scale-105 transition-all duration-300 group">
              <div className="bg-gradient-to-br from-purple/20 to-gold/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:from-purple/30 group-hover:to-gold/30 transition-colors">
                <Star className="text-gold" size={32} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Lifetime Warranty</h3>
              <p className="text-white/60 leading-relaxed">Premium quality backed by a lifetime warranty against fading, bubbling, and peeling.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section id="gallery" className="py-24 bg-surface">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">LC Customs Gallery</h2>
            <p className="text-white/60 text-lg">Premium Results That Speak for Themselves</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayGallery.map((image, index) => (
              <div key={image.id} className={`relative overflow-hidden rounded-2xl group cursor-pointer border border-gold/10 hover:border-gold/30 transition-colors ${index === 0 ? 'lg:col-span-2 lg:row-span-2' : ''}`}>
                <img 
                  src={image.image_url} 
                  alt={image.alt_text} 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  style={{ minHeight: index === 0 ? '500px' : '300px' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-6 left-6">
                    <p className="text-white font-semibold text-lg">{image.alt_text}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section id="reviews" className="py-24 bg-base">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">What Our Clients Say</h2>
            <p className="text-white/60 text-lg mb-2">Trusted by Car Enthusiasts in Chicago</p>
            <p className="text-gold text-sm font-semibold">★ All Real Paying Customers ★</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayReviews.map((review) => (
              <div key={review.id} className="bg-white/5 backdrop-blur-md border border-gold/10 rounded-2xl p-8 hover:border-purple/30 transition-all">
                <div className="flex gap-1 mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="text-gold fill-gold" size={22} strokeWidth={2} />
                  ))}
                </div>
                <p className="text-white/80 mb-4 leading-relaxed">"{review.comment}"</p>
                <p className="text-gold font-semibold">- {review.customer_name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <section id="quote" className="py-24 bg-surface">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">Get Your Free Quote</h2>
            <p className="text-white/60 text-lg">Fill out the form below and we'll get back to you within 24 hours</p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white/5 backdrop-blur-md border border-gold/10 rounded-2xl p-8 space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-white mb-2 font-medium">Your Name</label>
                <Input 
                  name="customer_name" 
                  value={formData.customer_name} 
                  onChange={handleInputChange} 
                  required 
                  data-testid="quote-name-input"
                  className="bg-white/5 border-gold/20 text-white placeholder-white/30 focus:border-purple focus:ring-purple"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-white mb-2 font-medium">Email</label>
                <Input 
                  type="email" 
                  name="email" 
                  value={formData.email} 
                  onChange={handleInputChange} 
                  required 
                  data-testid="quote-email-input"
                  className="bg-white/5 border-gold/20 text-white placeholder-white/30 focus:border-purple focus:ring-purple"
                  placeholder="john@example.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-white mb-2 font-medium">Phone Number</label>
              <Input 
                type="tel" 
                name="phone" 
                value={formData.phone} 
                onChange={handleInputChange} 
                required 
                data-testid="quote-phone-input"
                className="bg-white/5 border-gold/20 text-white placeholder-white/30 focus:border-purple focus:ring-purple"
                placeholder="(773) 815-1709"
              />
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <label className="block text-white mb-2 font-medium">Year</label>
                <Input 
                  name="year" 
                  value={formData.year} 
                  onChange={handleInputChange} 
                  required 
                  data-testid="quote-year-input"
                  className="bg-white/5 border-gold/20 text-white placeholder-white/30 focus:border-purple focus:ring-purple"
                  placeholder="2024"
                />
              </div>
              <div>
                <label className="block text-white mb-2 font-medium">Make</label>
                <Input 
                  name="make" 
                  value={formData.make} 
                  onChange={handleInputChange} 
                  required 
                  data-testid="quote-make-input"
                  className="bg-white/5 border-gold/20 text-white placeholder-white/30 focus:border-purple focus:ring-purple"
                  placeholder="Mercedes"
                />
              </div>
              <div>
                <label className="block text-white mb-2 font-medium">Model</label>
                <Input 
                  name="model" 
                  value={formData.model} 
                  onChange={handleInputChange} 
                  required 
                  data-testid="quote-make-input"
                  className="bg-white/5 border-gold/20 text-white placeholder-white/30 focus:border-purple focus:ring-purple"
                  placeholder="G-Wagon"
                />
              </div>
            </div>

            <div>
              <label className="block text-white mb-2 font-medium">Service Type</label>
              <Select onValueChange={handleSelectChange} required>
                <SelectTrigger data-testid="quote-service-select" className="bg-white/5 border-gold/20 text-white focus:border-purple focus:ring-purple">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent className="bg-card border-gold/20">
                  <SelectItem value="full-car">Full Car Tint</SelectItem>
                  <SelectItem value="front-two">Front Two Windows</SelectItem>
                  <SelectItem value="windshield">Windshield Tint</SelectItem>
                  <SelectItem value="sunroof">Sunroof Tint</SelectItem>
                  <SelectItem value="custom">Custom Package</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-white mb-2 font-medium">Additional Notes (Optional)</label>
              <Textarea 
                name="additional_notes" 
                value={formData.additional_notes} 
                onChange={handleInputChange} 
                data-testid="quote-notes-input"
                className="bg-white/5 border-gold/20 text-white placeholder-white/30 focus:border-purple focus:ring-purple min-h-[100px]"
                placeholder="Any specific requirements or questions?"
              />
            </div>

            <Button 
              type="submit" 
              disabled={isSubmitting} 
              data-testid="quote-submit-btn"
              className="w-full bg-gradient-to-r from-purple to-purple-dark hover:from-purple-dark hover:to-purple text-white rounded-full py-6 text-lg font-bold shadow-lg shadow-purple/40 hover:shadow-purple/60 transition-all"
            >
              {isSubmitting ? "Submitting..." : "Get My Free Quote"}
            </Button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-gold/10 py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_lc-customs-tint/artifacts/j8hadcd4_IMG_1953.jpeg" 
                alt="LC Customs Logo" 
                className="h-16 w-auto mb-4"
              />
              <p className="text-white/60">Elite window tinting services in Chicago by LC Customs. Your luxury, our priority.</p>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Contact</h4>
              <div className="space-y-3">
                <a href="tel:7738151709" className="flex items-center gap-2 text-white/60 hover:text-gold transition-colors">
                  <Phone size={18} />
                  (773) 815-1709
                </a>
                <a href="mailto:loschicoscustomss@gmail.com" className="flex items-center gap-2 text-white/60 hover:text-gold transition-colors">
                  <Mail size={18} />
                  loschicoscustomss@gmail.com
                </a>
                <div className="flex items-center gap-2 text-white/60">
                  <MapPin size={18} />
                  Chicago Area
                </div>
              </div>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Hours</h4>
              <div className="text-white/60 space-y-2">
                <p>Monday - Friday: 9AM - 7PM</p>
                <p>Saturday: 10AM - 6PM</p>
                <p>Sunday: By Appointment</p>
              </div>
            </div>
          </div>
          <div className="border-t border-gold/10 pt-8 text-center">
            <p className="text-white/40 text-sm">© 2024 LC Customs. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;